// SINGLE source of notification click routing. Both the in-app notification
// list (NotificationsModal) and the web-push landing handler (Dashboard,
// resolving /dashboard?notification=<id>) call this so the two entry points
// can never disagree on where a given notification type takes the user.
export function notificationRoute(notification) {
  const notificationType = notification.notification_type;

  // Expense-lifecycle alerts (submitted / approved / rejected / claimed /
  // prepared / paid / released). They carry the expense's CLAIM TASK as task_id,
  // and GET /api/notifications resolves pending_tasks.expense_id back through
  // it — so the presence of expense_id, not a type prefix, is what identifies
  // them. Checked before the task_id fallback below, which would otherwise send
  // every one of them to Task Tracking (where a reviewed claim no longer shows).
  if (notification.expense_id) {
    return { path: '/expenses', state: { fromNotification: true, focusExpenseId: notification.expense_id } };
  }

  // Platform order notifications - navigate to platform management.
  //
  // The id comes from platform_order_id, NOT order_id. These alerts key on
  // task_id precisely because notifications.order_id FKs to orders(id) and a
  // platform_order id there is either rejected or silently attached to an
  // unrelated CUSTOMER order; the server resolves the platform order back
  // through the task and returns it as platform_order_id. (Reading order_id
  // here once meant every platform notification navigated with `undefined`
  // and opened nothing; no row relies on that fallback any more — audit D-19.)
  //
  // Matching also covers the GENERIC task types (task_claimed_yours,
  // task_progress, task_released) when they belong to a platform task: the
  // submitter's order lives on /platform, not in Task Tracking, and the type
  // alone can't tell them apart — the resolved platform_order_id can.
  //
  // WHY /platform AND NOT Task Tracking (audit D-24): the handler's own actions
  // — Done Trade, revoke, abandon, the order's payment slips — live in the order
  // modal on /platform; Task Tracking's platform card mirrors the order and
  // offers only the finance steps and the approve / reject decision. The pool
  // card's "Open in Task Tracking" is the task-record view of the same work.
  if ((notificationType && notificationType.startsWith('platform_order_')) ||
      notification.platform_order_id) {
    return {
      path: '/platform',
      state: {
        selectedPlatformOrderId: notification.platform_order_id,
        fromNotification: true
      }
    };
  }

  // Customer takeover / reassignment notifications → Customer Management.
  // A "requested" notification goes to the approving person-in-charge (open the
  // pending-requests panel to approve/reject); a "resolved" one goes to the
  // requester (focus that customer). Legacy rows created before this change are
  // type 'system' with a "Customer takeover" / "Takeover" title and carry no
  // customer_id — still route them to the right page (best effort, no focus).
  const title = notification.title || '';
  const isTakeoverRequest =
    notificationType === 'customer_takeover_requested' ||
    (notificationType === 'system' && title.startsWith('Customer takeover'));
  const isTakeoverResolved =
    notificationType === 'customer_takeover_resolved' ||
    (notificationType === 'system' && title.startsWith('Takeover'));
  if (isTakeoverRequest) {
    // Route to the pending-requests panel only — the next action (approve/
    // reject) lives there. Deliberately NOT focusing the customer, which would
    // open the full-screen details modal on top of and hide that panel.
    return {
      path: '/customers',
      state: {
        openReassignments: true,
        fromNotification: true
      }
    };
  }
  if (isTakeoverResolved) {
    return {
      path: '/customers',
      state: {
        focusCustomerId: notification.customer_id || undefined,
        fromNotification: true
      }
    };
  }

  // VIP upgrade notifications → Customer Management. A "requested" one opens
  // the pending VIP-requests panel (admin approves); a "resolved" one focuses
  // the customer for the requesting sales user.
  if (notificationType === 'customer_vip_requested') {
    return { path: '/customers', state: { openVipRequests: true, fromNotification: true } };
  }
  if (notificationType === 'customer_vip_resolved') {
    return { path: '/customers', state: { focusCustomerId: notification.customer_id || undefined, fromNotification: true } };
  }

  // Every remaining task-backed alert — payment requests, and the generic
  // "payment received" (PR) receipts sales sends finance — opens the full task
  // record in Task Tracking, where the details, the proof and every tranche
  // receipt live.
  //
  // WHY NOT THE ORDER, for a payment_received: it has none. That flow is
  // CUSTOMER-scoped, not order-scoped (POST /api/tasks/payment-received writes
  // customer_id and a receipt, never a platform_order_id), so there is no order
  // to open. Anything that DOES belong to a platform order was already routed
  // above by the resolved platform_order_id — including its task_new alert —
  // so reaching here means there genuinely is no order behind it.
  //
  // focusTaskId always lands Task Tracking on its All tab (status filter "All"),
  // the one list that holds every task whoever has it, claimed or not: a "new
  // task" alert points at a task nobody has claimed yet, and focusing only the
  // user's own lists silently did nothing.
  if (notification.task_id) {
    return {
      path: '/task-tracking',
      state: { focusTaskId: notification.task_id }
    };
  }

  // Regular order notifications - navigate to order history
  if (notification.order_id) {
    return {
      path: '/order-history',
      state: { selectedOrderId: notification.order_id }
    };
  }

  // System notifications or other types with no specific navigation
  return null;
}
